/**
 * Time-of-day helpers for the resident planner (spec 2026-10-06 §4–§5).
 *
 * Times are floating 'HH:MM' keys: 24-hour, zero-padded, no zone — "19:00" is 7:00 PM on whatever
 * clock the device has. Zero-padding makes string order chronological, so times compare as
 * strings. Pure: nothing here reads the clock; callers pass "now" as keys.
 *
 * Imports types only: todoCalendar's sortTodos imports compareTodos from here, so importing
 * todoCalendar back would be a require cycle (Metro warns on those).
 */
import type { ResidentTodo } from '@/types/residents/todos';

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * 'HH:MM' → "7:05 PM". By hand, not Intl: Hermes, JSC and browsers format times differently, and
 * the planner pins en-US like the other formatters.
 */
export function formatTime(timeKey: string): string {
  const [h, m] = timeKey.split(':').map(Number);
  return `${h % 12 || 12}:${pad2(m)} ${h < 12 ? 'AM' : 'PM'}`;
}

/** A local Date's time as 'HH:MM' (seconds dropped). */
export const toTimeKey = (d: Date): string => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

/** 'YYYY-MM-DD' + 'HH:MM' → that local moment: the Date a time picker shows. */
export function timeOnDate(dateKey: string, timeKey: string): Date {
  const [y, mo, d] = dateKey.split('-').map(Number);
  const [h, mi] = timeKey.split(':').map(Number);
  return new Date(y, mo - 1, d, h, mi);
}

/**
 * A day's order: timed todos first, earliest first, then untimed ones; ties by title. Completion
 * is deliberately NOT a key — a ticked row keeps its slot instead of jumping, so the list never
 * reshuffles under the resident's finger.
 */
export function compareTodos(a: ResidentTodo, b: ResidentTodo): number {
  if (a.dueTime && b.dueTime) {
    if (a.dueTime !== b.dueTime) return a.dueTime < b.dueTime ? -1 : 1;
  } else if (a.dueTime) {
    return -1;
  } else if (b.dueTime) {
    return 1;
  }
  return a.title.localeCompare(b.title);
}

/**
 * Unticked, timed, and its day and time are before now — today once the minute after its time
 * begins, any day before today at any time. Judged on the day and time the item SHOWS (a moved
 * date's own). Day lists only: Overdue rows are late by definition.
 */
export const isLate = (todo: ResidentTodo, nowDateKey: string, nowTimeKey: string): boolean =>
  !todo.completed &&
  !!todo.dueTime &&
  (todo.dueDate < nowDateKey || (todo.dueDate === nowDateKey && todo.dueTime < nowTimeKey));

/**
 * Where the "now" line sits in a day's list (spec 2026-10-07 §4.1): above the first row that has
 * not passed — the first untimed row, or the first timed at or after now — so `length` once every
 * row is timed and passed. Null when there is no line: not today, or no row has a time. Expects
 * sortTodos order. Completion is not a key (as in compareTodos), and "passed" is isLate's `<`, so
 * the line and the red ink never disagree.
 */
export function nowLineIndex(
  dayTodos: ResidentTodo[],
  dayKey: string,
  todayKey: string,
  nowTimeKey: string,
): number | null {
  if (dayKey !== todayKey || !dayTodos.some(t => t.dueTime)) return null;
  const index = dayTodos.findIndex(t => !t.dueTime || t.dueTime >= nowTimeKey);
  return index === -1 ? dayTodos.length : index;
}

/**
 * The day heading's spoken "next at …" (spec 2026-10-07 §6): the first unticked todo timed at or
 * after now, on today only. A todo ticked early is not "next".
 */
export function nextTimedTodo(
  dayTodos: ResidentTodo[],
  dayKey: string,
  todayKey: string,
  nowTimeKey: string,
): ResidentTodo | null {
  if (dayKey !== todayKey) return null;
  return dayTodos.find(t => !t.completed && !!t.dueTime && t.dueTime >= nowTimeKey) ?? null;
}

/** What "At time" fills in: the next whole hour for today (11 PM at the latest), else 9 AM. */
export function defaultTime(dateKey: string, todayKey: string, nowTimeKey: string): string {
  if (dateKey !== todayKey) return '09:00';
  const hour = Number(nowTimeKey.slice(0, 2));
  return `${pad2(Math.min(hour + 1, 23))}:00`;
}
